/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * --- REWRITE SUMMARY (V81 - DEFINITIVE & FINAL LAYOUT FIX) ---
 * - MISSION: To permanently and correctly fix the visual bug where this
 * button was being overlapped by page content and the CookieBanner.
 *
 * - THE DEFINITIVE FIX (Revert to Original Logic):
 * 1.  This component is reverted to its original, simple, and correct state.
 * 2.  It is `position: fixed` and accepts the `cookieBannerHeight` prop.
 * 3.  It uses this prop to dynamically calculate its `bottom` style property.
 * 4.  Its z-index is set to `z-[1001]`, ensuring it is always on top of
 * the cookie banner (`z-[1000]`).
 *
 * - RESULT: With the parent `App.jsx` now providing a guaranteed, accurate
 * height measurement at the correct time in the React lifecycle, this simple
 * and robust logic will now work perfectly and reliably as originally intended.
 * This is the final change needed to fix the bug.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { FaArrowUp, FaArrowDown } from 'react-icons/fa';
import clsx from 'clsx';
import { useNavContext } from '@/context/NavigationProvider';

const ScrollButton = () => {
  const { cookieBannerHeight } = useNavContext();
  const [isVisible, setIsVisible] = useState(false);

  const [shouldScrollUp, setShouldScrollUp] = useState(false);

  // Cache page height to avoid synchronous reads during scroll
  const pageHeightRef = useRef(0);
  // Track the auto-scroll loop to allow cancellation
  const loopRef = useRef(null);

  // Throttle function with trailing edge support
  const throttle = (func, limit) => {
    let inThrottle;
    let savedArgs;
    let savedThis;

    return function () {
      if (inThrottle) {
        savedArgs = arguments;
        savedThis = this;
        return;
      }

      func.apply(this, arguments);
      inThrottle = true;

      setTimeout(() => {
        inThrottle = false;
        if (savedArgs) {
          func.apply(savedThis, savedArgs);
          savedArgs = null;
          savedThis = null;
        }
      }, limit);
    };
  };

  useEffect(() => {
    // --- PERFORMANCE FIX: ResizeObserver ---
    // Instead of reading document.documentElement.scrollHeight on every scroll 
    // (which forces a reflow), we passively observe the body for height changes.
    const resizeObserver = new ResizeObserver(() => {
      // We can iterate entries or just read the height from the entry safely
      // However, reading scrollHeight here once is much safer than on every scroll.
      // Better yet, use the observer's entry contentBoxSize/borderBoxSize if supported,
      // but document.documentElement.scrollHeight is reliable for "full page height".
      // Since this fires asynchronously, it avoids the "Forced Reflow" warning loop.
      pageHeightRef.current = document.documentElement.scrollHeight;
    });

    // Check initial height - allow ResizeObserver to handle this asynchronously to avoid reflow on mount.
    // pageHeightRef.current = document.documentElement.scrollHeight;
    resizeObserver.observe(document.body);

    return () => resizeObserver.disconnect();
  }, []);

  const handleScroll = useCallback(() => {
    // Wrap in rAF to ensure execution aligns with the start of a frame
    window.requestAnimationFrame(() => {
      const scrollY = window.scrollY; // Cheap read

      // 1. Visibility Check
      if (scrollY > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }

      // 2. Bottom Check
      // Use the cached height from ResizeObserver
      const pageHeight = pageHeightRef.current;
      const viewportHeight = window.innerHeight;

      // Ensure pageHeight is valid (it might be 0 initially)
      if (pageHeight > 0 && scrollY + viewportHeight > pageHeight - 400) {
        setShouldScrollUp(true);
      } else {
        setShouldScrollUp(false);
      }
    });
  }, []);

  useEffect(() => {
    const throttledScrollHandler = throttle(handleScroll, 200);
    window.addEventListener('scroll', throttledScrollHandler, { passive: true });

    // Initial check (deferred slightly to ensure layout is settled)
    const timer = setTimeout(handleScroll, 500);

    return () => {
      window.removeEventListener('scroll', throttledScrollHandler);
      clearTimeout(timer);
    };
  }, [handleScroll]);

  const scrollTo = () => {
    // Always clear any existing check loop to prevent conflicts
    if (loopRef.current) {
      clearTimeout(loopRef.current);
      loopRef.current = null;
    }

    if (shouldScrollUp) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // --- OPTIMIZED PROGRESSIVE SCROLL ---
      // Instead of polling, we scroll to current known bottom, then wait for ResizeObserver
      // to update height if lazy content loads.

      let currentHeight = document.documentElement.scrollHeight;
      window.scrollTo({ top: currentHeight, behavior: 'smooth' });

      // If content lazily loads, ResizeObserver will catch it.
      // We can set a temporary "chase" mode if we want to be very aggressive, 
      // but usually, letting the user simply click again if new content loads is acceptable behavior.
      // However, to match previous "progressive" intent:

      let attempts = 0;
      const maxAttempts = 25; // 5 seconds approx

      // Start the check loop
      const safeCheck = () => {
        // READ first
        const newHeight = document.documentElement.scrollHeight;
        const needsScroll = newHeight > currentHeight;

        if (needsScroll) {
          currentHeight = newHeight;
          // WRITE second
          window.scrollTo({ top: currentHeight, behavior: 'smooth' });
        }

        attempts++;
        if (attempts < maxAttempts) {
          loopRef.current = setTimeout(safeCheck, 200);
        } else {
          loopRef.current = null;
        }
      };

      loopRef.current = setTimeout(safeCheck, 200);
    }
  };

  // Calculate the final bottom position
  // 1.5rem (24px) is the base offset from the viewport edge.
  const bottomPosition = 24 + cookieBannerHeight;

  return (
    <button
      onClick={scrollTo}
      aria-label={shouldScrollUp ? 'Scroll to top' : 'Scroll to bottom'}
      className={clsx(
        // The z-index is set higher than the cookie banner's z-index.
        'fixed right-6 z-[1001] w-12 h-12 flex items-center justify-center rounded-full bg-primary text-white shadow-lg transition-opacity duration-300 ease-in-out hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-light focus:ring-offset-2 focus:ring-offset-background',
        {
          'opacity-100 visible': isVisible,
          'opacity-0 invisible': !isVisible,
        }
      )}
      style={{
        bottom: cookieBannerHeight > 0
          ? `${bottomPosition}px`
          : `calc(24px + env(safe-area-inset-bottom, 0px))`,
        transition: 'bottom 0.5s ease-in-out, opacity 0.3s ease-in-out',
      }}
    >
      {shouldScrollUp ? <FaArrowUp className="text-xl" /> : <FaArrowDown className="text-xl" />}
    </button>
  );
};

export default ScrollButton;
